import { useEffect, useMemo, useState } from 'react';
import Reveal from '../components/Reveal';
import { audienceMembershipAr, audienceMembershipEn } from '../content/audienceMembershipContent';
import { useLanguage } from '../context/LanguageContext';

function AudiencePage() {
  const { t, language, isArabic } = useLanguage();
  const membership = language === 'ar' ? audienceMembershipAr : audienceMembershipEn;
  const app = t.audiencePage.applicationSection;
  const segmentImages = [
    'https://static.vecteezy.com/system/resources/thumbnails/068/575/434/small/a-group-of-men-in-suits-posing-for-a-photo.jpeg',
    'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1600',
    'https://images.pexels.com/photos/4386398/pexels-photo-4386398.jpeg?auto=compress&cs=tinysrgb&w=1600',
    'https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg?auto=compress&cs=tinysrgb&w=1600',
  ];

  const [categoryIndex, setCategoryIndex] = useState(null);
  const [basic, setBasic] = useState({
    fullName: '',
    country: '',
    city: '',
    whatsapp: '',
    email: '',
  });
  const [professional, setProfessional] = useState({
    license: '',
    portfolio: '',
    impact: '',
  });
  const [interests, setInterests] = useState(() => app.interestOptions.map(() => false));
  const [motivation, setMotivation] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const [step, setStep] = useState(0);
  const [membershipTierIndex, setMembershipTierIndex] = useState(0);

  const activeMembershipTier = membership.tiers[membershipTierIndex] || membership.tiers[0];

  const goMembershipTier = (delta) => {
    setMembershipTierIndex(
      (current) => {
        const next = (current + delta + membership.tiers.length) % membership.tiers.length;
        setCategoryIndex(next);
        return next;
      }
    );
  };

  const selectMembershipTier = (index) => {
    setMembershipTierIndex(index);
    setCategoryIndex(index);
  };

  const membershipPrevIcon = isArabic ? 'chevron_right' : 'chevron_left';
  const membershipNextIcon = isArabic ? 'chevron_left' : 'chevron_right';

  const showProfessionalStep = false;

  const steps = useMemo(() => {
    const base = ['category', 'basic'];
    if (showProfessionalStep) base.push('professional');
    base.push('interests', 'motivation', 'confirmation');
    return base;
  }, [showProfessionalStep]);

  const totalSteps = steps.length;

  useEffect(() => {
    setStep((current) => Math.min(current, Math.max(totalSteps - 1, 0)));
  }, [totalSteps]);

  const stepKey = steps[step] || 'category';

  const canContinue = useMemo(() => {
    switch (stepKey) {
      case 'category':
        return categoryIndex !== null;
      case 'basic':
        return (
          basic.fullName.trim() &&
          basic.country.trim() &&
          basic.city.trim() &&
          basic.whatsapp.trim() &&
          basic.email.trim()
        );
      case 'professional':
        return (
          professional.license.trim() &&
          professional.portfolio.trim() &&
          professional.impact.trim()
        );
      case 'interests':
        return interests.some(Boolean);
      case 'motivation':
        return motivation.trim().length >= 12;
      case 'confirmation':
        return confirmed;
      default:
        return false;
    }
  }, [stepKey, categoryIndex, basic, professional, interests, motivation, confirmed]);

  const stepProgressLabel = app.stepProgress
    .replace('{current}', String(step + 1))
    .replace('{total}', String(totalSteps));

  const goNext = () => {
    if (!canContinue) return;
    setStep((s) => Math.min(s + 1, totalSteps - 1));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <main className="inner-page">
      <section className="container audience-hero">
        <div className="audience-hero-split">
          <Reveal direction="left">
            <div className="audience-hero-card">
              <span className="eyebrow">{t.audiencePage.eyebrow}</span>
              <h1>{t.audiencePage.title}</h1>
              <p>{t.audiencePage.subtitle}</p>
            </div>
          </Reveal>
          <Reveal direction="right">
            <div className="audience-hero-shot">
              <img
                src={`${process.env.PUBLIC_URL}/images/membershipInside.jfif`}
                alt={t.audiencePage.title}
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container audience-membership-overview">
        <Reveal>
          <h2 className="center-title">{t.audiencePage.who}</h2>
        </Reveal>
        <div className="audience-membership-grid">
          <Reveal direction="left">
            <article className="membership-concept">
              <div className="membership-concept-head">
                <span className="material-symbols-outlined audience-icon" aria-hidden="true">
                  workspace_premium
                </span>
                <h3>{t.audiencePage.conceptTitle}</h3>
              </div>
              <p>{t.audiencePage.conceptText}</p>
            </article>
          </Reveal>
          <Reveal direction="right">
            <div className="audience-membership-visuals" aria-hidden="true">
              <div className="audience-membership-photo audience-membership-photo-main" />
              <div className="audience-membership-photo audience-membership-photo-sub" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container audience-benefits">
        <Reveal>
          <h2 className="center-title">{t.audiencePage.benefitsTitle}</h2>
        </Reveal>
        <div className="cards four audience-cards">
          {t.audiencePage.cards.map((item, i) => (
            <Reveal key={item.title} delay={i * 120}>
              <article className="card audience-card">
                <div
                  className="audience-card-image"
                  style={{ backgroundImage: `url(${segmentImages[i] || segmentImages[0]})` }}
                  aria-hidden="true"
                />
                <div className="audience-head">
                  <span className="material-symbols-outlined audience-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <h3>{item.title}</h3>
                </div>
                <p>{item.text}</p>
                <ul className="bullets">
                  {item.bullets.map((b) => (
                    <li key={b}>
                      <span className="material-symbols-outlined check">check_circle</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section audience-story">
        <div className="container">
          <Reveal>
            <h2 className="center-title">{t.audiencePage.storyTitle}</h2>
          </Reveal>
          <div className="audience-story-grid">
            <Reveal direction="left">
              <div className="audience-story-image audience-story-image-1" aria-hidden="true" />
            </Reveal>
            <Reveal direction="up">
              <div className="audience-story-image audience-story-image-2" aria-hidden="true" />
            </Reveal>
            <Reveal direction="right">
              <div className="audience-story-image audience-story-image-3" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section membership-showcase">
        <div className="membership-showcase-bg" aria-hidden="true" />
        <div className="container membership-showcase-inner">
          <Reveal>
            <header className="membership-showcase-header">
              <div className="membership-showcase-title-box">
                <h2>{membership.title}</h2>
              </div>
              <div className="membership-showcase-intro">
                {membership.intro.map((paragraph) => (
                  <p key={paragraph.slice(0, 32)}>{paragraph}</p>
                ))}
              </div>
            </header>
          </Reveal>

          <div className="membership-showcase-picker" role="tablist" aria-label={membership.title}>
            {membership.tiers.map((tier, index) => (
              <button
                key={tier.id}
                type="button"
                role="tab"
                aria-selected={membershipTierIndex === index}
                className={`membership-showcase-plan membership-showcase-plan--${tier.id}${
                  membershipTierIndex === index ? ' is-active' : ''
                }`}
                onClick={() => selectMembershipTier(index)}
              >
                {tier.thumb ? (
                  <span className="membership-showcase-plan-photo">
                    <img src={tier.thumb} alt="" />
                  </span>
                ) : null}
                <span className="material-symbols-outlined membership-showcase-plan-icon" aria-hidden="true">
                  {tier.icon}
                </span>
                <span className="membership-showcase-plan-name">{tier.label}</span>
                <span className="membership-showcase-plan-price">{tier.annualFee}</span>
              </button>
            ))}
          </div>

          <Reveal key={activeMembershipTier.id}>
            <article
              className={`membership-showcase-stage membership-showcase-stage--${activeMembershipTier.id}`}
              role="tabpanel"
              aria-live="polite"
            >
              <div className="membership-showcase-hero">
                <div
                  className="membership-showcase-hero-image"
                  style={{ backgroundImage: `url(${activeMembershipTier.image})` }}
                  role="img"
                  aria-label={activeMembershipTier.title}
                />
                <div className="membership-showcase-hero-overlay" aria-hidden="true" />
                <div className="membership-showcase-hero-body">
                  <span className="material-symbols-outlined membership-showcase-hero-icon" aria-hidden="true">
                    {activeMembershipTier.icon}
                  </span>
                  <h3>{activeMembershipTier.title}</h3>
                  <div className="membership-showcase-stats">
                    <div className="membership-showcase-stat">
                      <small>{membership.annualFeeLabel}</small>
                      <strong>{activeMembershipTier.annualFee}</strong>
                    </div>
                    <div className="membership-showcase-stat">
                      <small>{membership.operationsLabel}</small>
                      <strong>{activeMembershipTier.operations}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="membership-showcase-services">
                <div className="membership-showcase-services-grid">
                  {activeMembershipTier.services.map((row) => (
                    <article key={row.service} className="membership-showcase-service">
                      <div className="membership-showcase-service-head">
                        <span className="material-symbols-outlined" aria-hidden="true">
                          verified
                        </span>
                        <h4>{row.service}</h4>
                      </div>
                      <dl className="membership-showcase-service-details">
                        <div>
                          <dt>{membership.tableHeaders[1]}</dt>
                          <dd>{row.benefit}</dd>
                        </div>
                        <div>
                          <dt>{membership.tableHeaders[2]}</dt>
                          <dd>{row.value}</dd>
                        </div>
                      </dl>
                    </article>
                  ))}
                </div>
              </div>

              <div className="membership-showcase-core">
                <span className="material-symbols-outlined" aria-hidden="true">
                  workspace_premium
                </span>
                <div>
                  <strong>{activeMembershipTier.coreValueTitle}</strong>
                  <p>{activeMembershipTier.coreValue}</p>
                </div>
              </div>
            </article>
          </Reveal>

          <div className="membership-showcase-nav">
            <button
              type="button"
              className="membership-showcase-nav-btn"
              onClick={() => goMembershipTier(-1)}
              aria-label={isArabic ? 'العضوية السابقة' : 'Previous membership tier'}
            >
              <span className="material-symbols-outlined">{membershipPrevIcon}</span>
            </button>
            <div className="membership-showcase-dots" role="tablist" aria-label={membership.title}>
              {membership.tiers.map((tier, index) => (
                <button
                  key={tier.id}
                  type="button"
                  role="tab"
                  aria-selected={membershipTierIndex === index}
                  aria-label={tier.label}
                  className={membershipTierIndex === index ? 'is-active' : ''}
                  onClick={() => selectMembershipTier(index)}
                />
              ))}
            </div>
            <button
              type="button"
              className="membership-showcase-nav-btn"
              onClick={() => goMembershipTier(1)}
              aria-label={isArabic ? 'العضوية التالية' : 'Next membership tier'}
            >
              <span className="material-symbols-outlined">{membershipNextIcon}</span>
            </button>
          </div>

          <div className="membership-showcase-footnotes">
            <Reveal direction="left">
              <article className="membership-showcase-note">
                <h4>{membership.excludedTitle}</h4>
                {membership.excludedIntro ? (
                  <p className="membership-showcase-note-intro">{membership.excludedIntro}</p>
                ) : null}
                <ul>
                  {membership.excluded.map((row) => (
                    <li key={row.service}>
                      <strong>{row.service}</strong>
                      <span>{row.note}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
            <Reveal direction="right">
              <div className="membership-showcase-note-photo">
                <img
                  src={`${process.env.PUBLIC_URL}/images/specialservices.jfif`}
                  alt={membership.excludedTitle}
                />
              </div>
            </Reveal>
          </div>

          <article className="membership-showcase-note membership-showcase-note-highlight membership-showcase-summary">
            <h4>{membership.summaryTitle}</h4>
            <p>{membership.summary}</p>
          </article>
        </div>
      </section>

      <section id="join-application" className="section audience-application">
        <div className="container application-grid">
          <Reveal>
            <div className="application-intro">
              <span className="eyebrow">{app.title}</span>
            </div>
          </Reveal>

          <div className="application-lead">
            <Reveal direction="left">
              <div className="application-lead-copy">
                <h2 className="clubs-title">
                  <span>{app.subtitle}</span>
                  {app.subtitleSecond ? <span>{app.subtitleSecond}</span> : null}
                </h2>
                <p className="application-note">{app.intro}</p>
              </div>
            </Reveal>
            <Reveal direction="right">
              <div className="application-lead-image">
                <img
                  src={`${process.env.PUBLIC_URL}/images/membershipapplication.jfif`}
                  alt={app.title}
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={120} className="application-form-wrap">
            <form className="application-form" onSubmit={(e) => e.preventDefault()}>
              <div className="application-step-meta" aria-live="polite">
                <span className="application-step-progress">{stepProgressLabel}</span>
              </div>

              {stepKey === 'category' ? (
                <fieldset className="application-fieldset">
                  <legend>{app.sections.category}</legend>
                  <div className="application-membership-grid">
                    {membership.tiers.map((tier, idx) => (
                      <label
                        key={tier.id}
                        className={`application-membership-option application-membership-option--${tier.id}${
                          categoryIndex === idx ? ' is-selected' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="membership-tier"
                          checked={categoryIndex === idx}
                          onChange={() => selectMembershipTier(idx)}
                        />
                        <span className="material-symbols-outlined application-membership-icon" aria-hidden="true">
                          {tier.icon}
                        </span>
                        <span className="application-membership-name">{tier.label}</span>
                        <span className="application-membership-title">{tier.title}</span>
                        <span className="application-membership-price">{tier.annualFee}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ) : null}

              {stepKey === 'basic' ? (
                <fieldset className="application-fieldset">
                  <legend>{app.sections.basic}</legend>
                  <label>
                    <span>{app.basicFields.fullName}</span>
                    <input
                      type="text"
                      value={basic.fullName}
                      onChange={(e) => setBasic((s) => ({ ...s, fullName: e.target.value }))}
                      placeholder={app.basicFields.fullName}
                    />
                  </label>
                  <label>
                    <span>{app.basicFields.country}</span>
                    <input
                      type="text"
                      value={basic.country}
                      onChange={(e) => setBasic((s) => ({ ...s, country: e.target.value }))}
                      placeholder={app.basicFields.country}
                    />
                  </label>
                  <label>
                    <span>{app.basicFields.city}</span>
                    <input
                      type="text"
                      value={basic.city}
                      onChange={(e) => setBasic((s) => ({ ...s, city: e.target.value }))}
                      placeholder={app.basicFields.city}
                    />
                  </label>
                  <label>
                    <span>{app.basicFields.whatsapp}</span>
                    <input
                      type="text"
                      value={basic.whatsapp}
                      onChange={(e) => setBasic((s) => ({ ...s, whatsapp: e.target.value }))}
                      placeholder={app.basicFields.whatsapp}
                    />
                  </label>
                  <label>
                    <span>{app.basicFields.email}</span>
                    <input
                      type="email"
                      value={basic.email}
                      onChange={(e) => setBasic((s) => ({ ...s, email: e.target.value }))}
                      placeholder={app.basicFields.email}
                    />
                  </label>
                </fieldset>
              ) : null}

              {stepKey === 'professional' ? (
                <fieldset className="application-fieldset">
                  <legend>{app.sections.professional}</legend>
                  <label>
                    <span>{app.professionalFields.license}</span>
                    <input
                      type="text"
                      value={professional.license}
                      onChange={(e) => setProfessional((s) => ({ ...s, license: e.target.value }))}
                      placeholder={app.professionalFields.license}
                    />
                  </label>
                  <label>
                    <span>{app.professionalFields.portfolio}</span>
                    <input
                      type="text"
                      value={professional.portfolio}
                      onChange={(e) => setProfessional((s) => ({ ...s, portfolio: e.target.value }))}
                      placeholder={app.professionalFields.portfolio}
                    />
                  </label>
                  <label>
                    <span>{app.professionalFields.impact}</span>
                    <input
                      type="text"
                      value={professional.impact}
                      onChange={(e) => setProfessional((s) => ({ ...s, impact: e.target.value }))}
                      placeholder={app.professionalFields.impact}
                    />
                  </label>
                </fieldset>
              ) : null}

              {stepKey === 'interests' ? (
                <fieldset className="application-fieldset">
                  <legend>{app.sections.interests}</legend>
                  <p className="application-prompt">{app.interestPrompt}</p>
                  <div className="application-check-grid">
                    {app.interestOptions.map((option, idx) => (
                      <label key={option} className="application-check">
                        <input
                          type="checkbox"
                          checked={Boolean(interests[idx])}
                          onChange={() =>
                            setInterests((prev) => prev.map((v, i) => (i === idx ? !v : v)))
                          }
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ) : null}

              {stepKey === 'motivation' ? (
                <fieldset className="application-fieldset">
                  <legend>{app.sections.motivation}</legend>
                  <textarea
                    rows="4"
                    value={motivation}
                    onChange={(e) => setMotivation(e.target.value)}
                    placeholder={app.motivationPlaceholder}
                  />
                </fieldset>
              ) : null}

              {stepKey === 'confirmation' ? (
                <fieldset className="application-fieldset">
                  <legend>{app.sections.confirmation}</legend>
                  <label className="application-check">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                    />
                    <span>{app.confirmationLabel}</span>
                  </label>
                </fieldset>
              ) : null}

              <div className="application-step-actions">
                <button type="button" className="ghost-btn" onClick={goBack} disabled={step === 0}>
                  {app.back}
                </button>

                {step < totalSteps - 1 ? (
                  <button type="button" className="primary-btn" onClick={goNext} disabled={!canContinue}>
                    {app.next}
                  </button>
                ) : (
                  <button type="submit" className="primary-btn application-submit" disabled={!canContinue}>
                    {app.submit}
                  </button>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default AudiencePage;
